import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  IconButton,
  Switch,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import {
  useAddBeauticianSlotsMutation,
  useUpdateBeauticianSlotsMutation,
} from "slices/beautyApp/beauticianSlice";

function AddSlotModal({ val, open, onClose, initialData, onSave }) {
  const [tempSlots, setTempSlots] = useState([]);
  const [selectedDay, setSelectedDay] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [addSlot] = useAddBeauticianSlotsMutation();
  const [updateSlot] = useUpdateBeauticianSlotsMutation();

  const daysOfWeek = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  useEffect(() => {
    if (initialData) {
      setSelectedDay(initialData.day || "");
      setTempSlots(initialData.slots || []);
    } else {
      setSelectedDay("");
      setTempSlots([]);
    }
  }, [initialData]);

  useEffect(() => {
    if (!open) {
      setErrorMessage(""); // Clear error on modal close
      setTempSlots([]);
      setSelectedDay("");
    }
  }, [open]);

  const handleAddEmptySlot = () => {
    setTempSlots([...tempSlots, { start: "", end: "", active: true }]);
  };

  const handleChangeSlot = (index, field, value) => {
    const updatedSlots = [...tempSlots];
    updatedSlots[index][field] = value;
    setTempSlots(updatedSlots);
  };

  const handleDeleteSlot = (index) => {
    const updatedSlots = [...tempSlots];
    updatedSlots.splice(index, 1);
    setTempSlots(updatedSlots);
  };

  const handleToggleActive = (index) => {
    const updatedSlots = [...tempSlots];
    const updatedSlot = {
      ...updatedSlots[index],
      active: !updatedSlots[index].active,
    };
    updatedSlots[index] = updatedSlot;
    setTempSlots(updatedSlots);
  };

  const handleSave = async () => {
    setErrorMessage("");

    if (!selectedDay) {
      setErrorMessage("Please select a day.");
      return;
    }

    const invalidSlot = tempSlots.find((slot) => !slot.start || !slot.end);
    if (invalidSlot) {
      setErrorMessage("All slots must have start and end time.");
      return;
    }

    try {
      if (initialData?._id) {
        await updateSlot({
          id: initialData._id,
          body: {
            user: val?._id,
            day: selectedDay,
            slots: tempSlots,
            active: initialData.active,
          },
        }).unwrap();
      } else {
        await addSlot({
          user: val?._id,
          day: selectedDay,
          slots: tempSlots,
          active: true,
        }).unwrap();
      }

      Swal.fire("Success", "Slots saved successfully", "success");
      onSave(); // Close and refresh
    } catch (error) {
      setErrorMessage(
        error?.data?.message || "Something went wrong while saving slots.",
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add / Update Slots</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>Select Day</InputLabel>
          <Select
            value={selectedDay}
            label="Select Day"
            onChange={(e) => setSelectedDay(e.target.value)}
            disabled={!!initialData?.day} // Disable if updating
          >
            {daysOfWeek.map((day) => (
              <MenuItem key={day} value={day}>
                {day.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {tempSlots.map((slot, index) => (
          <Box key={index} display="flex" gap={1} alignItems="center" mt={2}>
            <TextField
              label="Start Time"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={slot.start}
              onChange={(e) => handleChangeSlot(index, "start", e.target.value)}
            />
            <TextField
              label="End Time"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={slot.end}
              onChange={(e) => handleChangeSlot(index, "end", e.target.value)}
            />
            <Tooltip title={slot.active ? "Active" : "Inactive"}>
              <Switch
                checked={slot.active}
                onChange={() => handleToggleActive(index)}
                size="small"
              />
            </Tooltip>
            <IconButton
              onClick={() => handleDeleteSlot(index)}
              sx={{ minWidth: 36, flexShrink: 0 }}
            >
              <DeleteIcon fontSize="small" style={{ color: "red" }} />
            </IconButton>
          </Box>
        ))}

        <Button onClick={handleAddEmptySlot} variant="outlined" sx={{ mt: 2 }}>
          + Add Slot
        </Button>

        {/* Show error message if exists */}
        {errorMessage && (
          <Typography color="error" mt={2} textAlign="center">
            {errorMessage}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={tempSlots.length === 0}
        >
          Save All
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddSlotModal;
